import { useAccessMatrix } from "@/lib/directory";
import { cn } from "@/lib/cn";

/**
 * Who can use what, across the estate.
 *
 * ⚠ The important distinction this view has to preserve: an empty cell means
 * "no access", a muted column means "that app isn't reporting". Collapsing the
 * two would quietly turn "we can't tell" into "nobody has access" — the sort of
 * answer someone would act on.
 */
export function AccessMatrix() {
  const { data, isPending } = useAccessMatrix();

  if (isPending) return <p className="text-muted-foreground text-sm">Loading…</p>;
  if (!data) return null;

  if (!data.configured) {
    return (
      <p className="border-border bg-muted text-muted-foreground rounded-lg border p-4 text-sm">
        <strong className="text-foreground">DIRECTORY_TOKEN is not set.</strong> Home
        cannot ask the other apps who has access, so this matrix is empty. Generate a
        shared token, set it here and on each app, and add the
        <code className="mx-1">/internal/directory/*</code> endpoints there — the
        contract is in the <code>double-layer-auth</code> skill.
      </p>
    );
  }

  const reporting = data.apps.filter((a) => a.roster !== null);
  const silent = data.apps.filter((a) => a.roster === null);

  return (
    <>
      <p className="text-muted-foreground mb-4 text-sm">
        {reporting.length} of {data.apps.length} apps are reporting.
        {silent.length > 0 && (
          <> Not yet wired up: {silent.map((a) => a.title).join(", ")}.</>
        )}
      </p>

      {data.emails.length === 0 ? (
        <p className="text-muted-foreground py-8 text-center text-sm">
          No app has reported a roster yet.
        </p>
      ) : (
        <div className="border-border bg-card overflow-x-auto rounded-lg border">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="text-muted-foreground text-xs">
                <th className="sticky left-0 bg-[var(--card)] px-4 py-2.5 font-medium">
                  Person
                </th>
                {data.apps.map((a) => (
                  <th
                    key={a.appId}
                    className={cn(
                      "px-3 py-2.5 font-medium whitespace-nowrap",
                      a.roster === null && "opacity-40",
                    )}
                    title={a.roster === null ? "Not reporting" : a.title}
                  >
                    {a.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.emails.map((email) => (
                <tr key={email} className="border-border border-t">
                  <td className="sticky left-0 bg-[var(--card)] px-4 py-2 whitespace-nowrap">
                    {email}
                  </td>
                  {data.apps.map((a) => {
                    if (a.roster === null) {
                      return (
                        <td
                          key={a.appId}
                          className="text-muted-foreground/40 px-3 py-2 text-center"
                          title="Not reporting"
                        >
                          ?
                        </td>
                      );
                    }
                    const entry = a.roster.find((u) => u.email === email);
                    if (!entry) {
                      return (
                        <td
                          key={a.appId}
                          className="text-muted-foreground/30 px-3 py-2 text-center"
                        >
                          —
                        </td>
                      );
                    }
                    return (
                      <td
                        key={a.appId}
                        className={cn(
                          "px-3 py-2 text-center text-xs whitespace-nowrap",
                          entry.deactivated
                            ? "text-destructive line-through"
                            : "text-foreground",
                        )}
                        title={entry.deactivated ? "Suspended in this app" : undefined}
                      >
                        {entry.role ?? "yes"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-muted-foreground mt-3 text-xs">
        “—” means no access. “?” means that app has not implemented the directory
        endpoints, so its access cannot be read.
      </p>
    </>
  );
}
